import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Link } from 'react-router-dom';
import { Plus } from 'lucide-react';
import { api, ApiError } from '@/lib/api';
import type { Organization, Task } from '@/lib/types';
import { TASK_STATUS_LABELS } from '@/lib/types';
import { MEMBER_TAGS, type OrgMember } from '@/lib/constants';
import { useYetki } from '@/lib/yetki';
import { MemberPicker, PageHeader } from '@/components/layout/PageHeader';
import { CrudActions } from '@/components/ui/CrudActions';
import { Badge, Button, Card, Input, Label, Select, Spinner, Textarea } from '@/components/ui';

interface UyeSecenegi {
  id: string;
  fullName: string;
  status?: string;
  tags?: string[];
}

interface GorevFormu {
  title: string;
  description: string;
  assigneeIds: string[];
}

const BOS_FORM: GorevFormu = { title: '', description: '', assigneeIds: [] };

/**
 * Görev ekleme ve düzenleme aynı form.
 *
 * Eskiden görev kartının içinde bütün katılımcılar bir liste hâlinde
 * duruyordu; sorumlu olan kişi seçili görünüyor, olmayanlar da yan yana
 * sıralanıyordu. Kartta artık yalnız sorumlular yazıyor, seçim bu forma
 * taşındı.
 */
function GorevForm({
  baslik,
  form,
  setForm,
  members,
  error,
  loading,
  onSubmit,
  onCancel,
}: {
  baslik: string;
  form: GorevFormu;
  setForm: (f: GorevFormu) => void;
  members: UyeSecenegi[];
  error: string;
  loading: boolean;
  onSubmit: (e: React.FormEvent) => void;
  onCancel: () => void;
}) {
  const [filterTag, setFilterTag] = useState('');

  return (
    <form onSubmit={onSubmit} className="space-y-4">
      <p className="font-serif text-sm font-semibold text-primary">{baslik}</p>
      <div>
        <Label>Görev</Label>
        <Input
          placeholder="ör. Çadır, Su, Ekmek"
          value={form.title}
          onChange={(e) => setForm({ ...form, title: e.target.value })}
          required
        />
      </div>
      <div>
        <Label>Açıklama</Label>
        <Textarea
          value={form.description}
          onChange={(e) => setForm({ ...form, description: e.target.value })}
        />
      </div>
      <div>
        <Label>Etikete göre filtrele</Label>
        <Select value={filterTag} onChange={(e) => setFilterTag(e.target.value)}>
          <option value="">Tüm katılımcılar</option>
          {MEMBER_TAGS.map((tag) => (
            <option key={tag} value={tag}>
              {tag}
            </option>
          ))}
        </Select>
      </div>
      <MemberPicker
        members={members}
        selectedIds={form.assigneeIds}
        onChange={(ids) => setForm({ ...form, assigneeIds: ids })}
        filterTag={filterTag || undefined}
      />
      {error && <p className="text-sm text-destructive">{error}</p>}
      <div className="flex gap-2">
        <Button type="submit" variant="gold" disabled={loading}>
          {loading ? <Spinner /> : 'Kaydet'}
        </Button>
        <Button type="button" variant="outline" onClick={onCancel}>
          İptal
        </Button>
      </div>
    </form>
  );
}

/** Görevin kimin üstünde olduğu — yalnız sorumlular, adlarıyla. */
function Sorumlular({ task }: { task: Task }) {
  const kisiler = task.assignees ?? [];
  if (!kisiler.length) {
    return <p className="text-sm text-muted-foreground">Sorumlu atanmadı</p>;
  }
  return (
    <div className="flex flex-wrap items-center gap-1.5">
      <span className="text-xs text-muted-foreground">Sorumlu:</span>
      {kisiler.map((k) => (
        <span
          key={k.id}
          className="rounded-full border border-accent/40 bg-accent/15 px-2.5 py-0.5 text-xs font-medium text-primary"
        >
          {k.fullName}
        </span>
      ))}
    </div>
  );
}

export function TasksPage() {
  const [showAdd, setShowAdd] = useState(false);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [form, setForm] = useState<GorevFormu>(BOS_FORM);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);

  const { data: orgs } = useQuery({
    queryKey: ['organizations'],
    queryFn: () => api<{ organizations: Organization[] }>('/organizations'),
  });

  const orgId = orgs?.organizations[0]?.id;
  /* Görevi yönetici kurar ve dağıtır; katılımcı yalnız üstündeki
     görevin durumunu ilerletir. API de aynı kuralı uyguluyor. */
  const { yonetebilir } = useYetki(orgId);

  const { data: membersData } = useQuery({
    queryKey: ['members', orgId],
    queryFn: () => api<{ members: OrgMember[] }>(`/organizations/${orgId}/members`),
    enabled: !!orgId,
  });

  const { data, isLoading, refetch } = useQuery({
    queryKey: ['tasks', orgId],
    queryFn: () => api<{ tasks: Task[] }>(`/tasks/org/${orgId}`),
    enabled: !!orgId,
  });

  const memberOptions: UyeSecenegi[] = (membersData?.members ?? []).map((m) => ({
    id: m.userId,
    fullName: m.fullName,
    status: m.status,
    tags: m.tags,
  }));

  function formuKapat() {
    setForm(BOS_FORM);
    setShowAdd(false);
    setEditingId(null);
    setError('');
  }

  function duzenlemeyeBasla(task: Task) {
    setShowAdd(false);
    setEditingId(task.id);
    setError('');
    setForm({
      title: task.title,
      description: task.description ?? '',
      assigneeIds: task.assignees?.map((a) => a.id) ?? [],
    });
  }

  async function kaydet(e: React.FormEvent) {
    e.preventDefault();
    if (!orgId) return;
    setError('');
    setLoading(true);
    try {
      const govde = JSON.stringify({
        title: form.title,
        description: form.description,
        assigneeIds: form.assigneeIds,
      });
      if (editingId) {
        await api(`/tasks/${editingId}`, { method: 'PATCH', body: govde });
      } else {
        await api(`/tasks/org/${orgId}`, { method: 'POST', body: govde });
      }
      formuKapat();
      await refetch();
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'Görev kaydedilemedi');
    } finally {
      setLoading(false);
    }
  }

  async function updateStatus(taskId: string, status: string) {
    await api(`/tasks/${taskId}`, { method: 'PATCH', body: JSON.stringify({ status }) });
    refetch();
  }

  async function deleteTask(taskId: string) {
    if (!confirm('Görevi silmek istediğinize emin misiniz?')) return;
    if (editingId === taskId) formuKapat();
    await api(`/tasks/${taskId}`, { method: 'DELETE' });
    refetch();
  }

  const statusVariant = (status: string) => {
    if (status === 'ready' || status === 'completed') return 'success' as const;
    if (status === 'preparing') return 'warning' as const;
    if (status === 'cancelled') return 'destructive' as const;
    return 'default' as const;
  };

  return (
    <div className="space-y-4">
      <PageHeader title="Görevler" subtitle="Organizasyon içi görev dağıtımı" />

      {yonetebilir && !showAdd && !editingId && (
        <>
          <Link to="/members" className="text-sm text-primary underline">
            Katılımcı etiketlerini düzenle (Aşçı, İmam, Muhasebe…)
          </Link>
          <div>
            <Button
              size="sm"
              variant="gold"
              onClick={() => {
                setForm(BOS_FORM);
                setEditingId(null);
                setError('');
                setShowAdd(true);
              }}
            >
              <Plus className="h-4 w-4" />
              Ekle
            </Button>
          </div>
        </>
      )}

      {showAdd && yonetebilir && (
        <Card>
          <GorevForm
            baslik="Yeni Görev"
            form={form}
            setForm={setForm}
            members={memberOptions}
            error={error}
            loading={loading}
            onSubmit={kaydet}
            onCancel={formuKapat}
          />
        </Card>
      )}

      {/* Form açıkken görev listesi gizleniyor; ekranda tek iş kalsın. */}
      {showAdd ? null : isLoading ? (
        <div className="flex justify-center py-12">
          <Spinner className="h-8 w-8" />
        </div>
      ) : (
        (data?.tasks ?? []).map((task) =>
          editingId === task.id ? (
            <Card key={task.id}>
              <GorevForm
                baslik="Görevi Düzenle"
                form={form}
                setForm={setForm}
                members={memberOptions}
                error={error}
                loading={loading}
                onSubmit={kaydet}
                onCancel={formuKapat}
              />
            </Card>
          ) : (
            <Card key={task.id}>
              <div className="flex items-start justify-between gap-3">
                <div>
                  <h3 className="font-serif font-semibold text-primary">{task.title}</h3>
                  {task.description && (
                    <p className="mt-1 text-sm text-muted-foreground">{task.description}</p>
                  )}
                </div>
                <div className="flex items-center gap-2">
                  <Badge variant={statusVariant(task.status)}>
                    {TASK_STATUS_LABELS[task.status] ?? task.status}
                  </Badge>
                  {yonetebilir && (
                    <CrudActions
                      onEdit={() => duzenlemeyeBasla(task)}
                      onDelete={() => deleteTask(task.id)}
                    />
                  )}
                </div>
              </div>
              <div className="mt-3">
                <Sorumlular task={task} />
              </div>
              <div className="mt-3 flex flex-wrap gap-1.5">
                {Object.entries(TASK_STATUS_LABELS).map(([key, label]) => (
                  <Button
                    key={key}
                    size="sm"
                    variant={task.status === key ? 'default' : 'outline'}
                    onClick={() => updateStatus(task.id, key)}
                  >
                    {label}
                  </Button>
                ))}
              </div>
            </Card>
          ),
        )
      )}
    </div>
  );
}
